import fetcher from '@/services/fetcher'
import { SettingsService } from '@/services/settings'
import { useAlertStore } from '@/stores/alert'
import { useCampaignsStore } from '@/stores/campaigns'
import { usePlansStore } from '@/stores/plans'
import { useUserStore } from '@/stores/user'
import type { Campaign, Nullable, Plan, Prize } from '@/types'
import { isInPast } from '@/utils/date-time-utils'
import {
  isCheckoutPageEnabled,
  isDepositPageEnabled,
  isLeaderboardEnabled,
} from '@/utils/feature-flags'
import { Icons } from '@/utils/helper-objects'
import { isPartnershipCampaign } from '@/utils/type-guard-utils'
import { toPortalUrl } from '@/utils/url-generation-utils'
import { isEmpty } from '@/utils/utils'
import { type SiteSlug, ThemeType, getTheme } from '@goodlifeplus/themes'
import { useUrlSearchParams } from '@vueuse/core'
import { useCookies } from '@vueuse/integrations/useCookies.mjs'
import type { RouteRecordProps } from 'env'
import {
  type NavigationGuardNext,
  type RouteLocation,
  type RouteRecordRaw,
  useRouter,
} from 'vue-router'
import { useToast } from 'vue-toastification'

import HomeLayoutProvider from '../views/Home/HomeLayoutProvider.vue'
import dailySpinsRoutes from './daily-spins-routes'
import { referralRedirectGuard } from './guards/referral-redirect'
import subscriptionPrizeAuthGuard from './guards/subscription-prize-auth'
import instantWinRoutes from './instant-win-routes'
import membersZoneRoutes from './members-zone-routes'
import termsAndConditionsRoutes from './terms-and-conditions-routes'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeLayoutProvider,
    meta: {
      nav: {
        sidebar: {
          display: true,
          text: 'Home',
          icon: Icons.Home,
          category: 'default',
          order: 0,
        },
        bottom: {
          display: true,
          text: 'nav.bottom.home',
          icon: Icons.Home,
        },
      },
    },
  },
  ...membersZoneRoutes,
  {
    path: '/product',
    name: 'product',
    component: () => import('../views/ProductView.vue'),
  },
  {
    path: '/subscription-platinum-35',
    name: 'affiliate-subscription-platinum-35',
    beforeEnter(to, _from, next) {
      if (to.meta.featuredCampaign) {
        return next({
          name: 'campaign',
          params: { campaignCode: to.meta.featuredCampaign.code },
        })
      }

      return next({ name: 'home' })
    },
  },
  {
    path: '/benefits',
    name: 'benefits',
    beforeEnter(_to, _from, next) {
      return next({ name: 'rewards', replace: true })
    },
  },
  {
    path: '/rewards',
    name: 'rewards',
    component: () => import('../views/RewardsView.vue'),
    meta: {
      title: 'Membership Rewards',
      nav: {
        bottom: {
          display: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription,
          text: 'nav.bottom.rewards',
          icon: Icons.Reward,
        },
        sidebar: {
          display: () => {
            return useUserStore().isLoggedOut
          },
          icon: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription
              ? Icons.Reward
              : Icons.Ticket,
          text: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription
              ? 'Rewards'
              : 'Points & Rewards',
          category: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription
              ? 'default'
              : 'third',
          order: 1,
        },
        footer: {
          display: true,
          text: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription
              ? 'Rewards'
              : 'Points & Rewards',
          category: 'company',
        },
      },
    },
  },
  {
    path: '/discounts',
    name: 'discounts',
    component: () => import('../views/DiscountsView.vue'),
    meta: {
      title: 'Discounts',
      nav: {
        bottom: {
          // Discounts only exist on the Subscription theme.
          display: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription,
          text: 'nav.bottom.discounts',
          icon: Icons.Ticket,
        },
        sidebar: {
          // Discounts only exist on the Subscription theme. The page and its
          // cards are shown to everyone; discount codes themselves are
          // masked in DiscountCard for members without an active pass/subscription.
          display: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription,
          icon: Icons.Ticket,
          text: 'Discounts',
          category: 'default',
          order: 2,
        },
      },
    },
  },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    component: () => import('../views/LeaderboardView.vue'),
    meta: {
      title: 'League Table',
      nav: {
        sidebar: {
          display: () =>
            isLeaderboardEnabled() &&
            useUserStore().isLoggedIn &&
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type !==
              ThemeType.Subscription,
          text: 'League Table',
          icon: Icons.LeaderBoard,
          category: 'default',
          order: 8,
        },
        footer: {
          display: () =>
            isLeaderboardEnabled() &&
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type !==
              ThemeType.Subscription,
          text: 'League Table',
          category: 'company',
        },
      },
    },
    beforeEnter: (_to, _from, next) => {
      if (
        isLeaderboardEnabled() &&
        getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type !==
          ThemeType.Subscription
      ) {
        next()
      } else {
        next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/prizes',
    name: 'prizes',
    component: () => import('../views/PrizesLayoutProvider.vue'),
    meta: {
      title: 'Prizes',
      nav: {
        bottom: {
          display: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription,
          text: 'nav.bottom.prizes',
          icon: Icons.Present,
        },
        footer: {
          display: true,
          text: 'Prizes',
          category: 'company',
        },
      },
    },
  },
  {
    path: '/prize/:prizeCode/:listId?',
    name: 'prize',
    component: () => import('../views/PrizeView.vue'),
    beforeEnter: [subscriptionPrizeAuthGuard],
    meta: {
      title: (
        to: RouteLocation & RouteRecordProps<{ prize: Prize; listId: string }>
      ): string => {
        return to.meta.props?.prize?.title
          ? `Prize: ${to.meta.props.prize.title}`
          : 'Prize'
      },
    },
    props: (route: RouteRecordProps<{ prize: Prize; listId: string }>) => {
      return {
        prize: route.meta.props.prize,
        listId: route.meta.props.listId,
      }
    },
  },
  {
    path: '/results-and-winners',
    name: 'results-and-winners',
    component: () => import('../views/ResultsLayoutProvider.vue'),
    meta: {
      title: 'Results + Winners',
      nav: {
        sidebar: {
          display: false,
        },
        footer: {
          display: true,
          text: 'Results + Winners',
          category: 'company',
        },
      },
    },
  },
  {
    path: '/give-back',
    name: 'give-back',
    component: () => import('../views/GiveBackView.vue'),
    meta: {
      title: 'Give Back',
      nav: {
        sidebar: {
          display: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription,
          icon: Icons.HandHeart,
          text: 'Give Back',
          category: () =>
            useUserStore().isLoggedIn ? 'members-zone' : 'second',
          order: 9,
        },
        footer: {
          display: false,
        },
      },
    },
  },
  {
    path: '/live-draws/:prizeCode',
    name: 'live-draw',
    component: () => import('../views/LiveDrawView.vue'),
    meta: {
      title: (
        to: RouteLocation & RouteRecordProps<{ prize: Prize }>
      ): string => {
        return to.meta.props?.prize?.title
          ? `Live Draw: ${to.meta.props.prize.title}`
          : 'Live Draw'
      },
    },
    props: (route: RouteRecordProps<{ prize: Prize }>) => {
      return {
        prize: route.meta.props.prize,
      }
    },
  },
  {
    path: '/thank-you',
    name: 'thank-you',
    component: () => import('../views/ThankYouView.vue'),
    meta: {
      title: 'Thank You',
    },
  },
  {
    path: '/good-causes',
    name: 'good-causes',
    component: () => import('../views/SponsorshipView.vue'),
    meta: {
      title: 'Good Causes',
      nav: {
        sidebar: {
          display: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type !==
            ThemeType.Subscription,
          text: 'Good Causes',
          icon: Icons.Diversity,
          category: 'third',
          order: 4,
        },
        footer: {
          display: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type !==
            ThemeType.Subscription,
          text: 'Good Causes',
          category: 'company',
        },
      },
    },
    beforeEnter: (_to, _from, next) => {
      if (
        getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
        ThemeType.Subscription
      ) {
        next({ name: 'not-found' })
      } else {
        next()
      }
    },
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('../views/faq/FaqView.vue'),
    meta: {
      title: 'Frequently Asked Questions',
      nav: {
        sidebar: {
          display: true,
          text: 'FAQs',
          icon: Icons.Question,
          category: () => {
            return useUserStore().isLoggedIn ? 'members-zone' : 'second'
          },
          order: 10,
        },
        footer: {
          display: true,
          text: 'FAQs',
          category: 'support',
        },
      },
    },
  },
  {
    path: '/faq/:slug',
    name: 'faq-details',
    component: () => import('../views/faq/FaqDetails.vue'),
    meta: {
      title: 'Frequently Asked Questions',
      nav: {
        footer: {
          display: false,
        },
      },
    },
  },
  {
    path: '/campaign/:campaignCode/:listId?',
    name: 'campaign',
    component: () => import('../views/CampaignView.vue'),
    meta: {
      title: (
        to: RouteLocation & RouteRecordProps<{ campaign: Campaign }>
      ): string => {
        return to.meta.props?.campaign?.name
          ? `Campaign: ${to.meta.props.campaign.name}`
          : 'Campaign'
      },
    },
    props: (
      route: RouteRecordProps<{ campaign: Campaign; listId?: string }>
    ) => {
      return {
        campaign: route.meta.props.campaign,
        listId: route.meta.props.listId,
      }
    },
    async beforeEnter(
      to: RouteLocation,
      _from: RouteLocation,
      next: NavigationGuardNext
    ) {
      try {
        const campaignCode = to.params.campaignCode as string | undefined
        const toast = useToast()

        if (!campaignCode) {
          return next({ name: 'not-found' })
        }

        const campaignStore = useCampaignsStore()
        const queryParams = useUrlSearchParams()

        const campaign = await campaignStore.fetchCampaign(campaignCode)

        if (!campaign) {
          toast.error('Campaign not found.')
          return next({ name: 'not-found' })
        }

        // Check if campaign close date is in the past
        if (campaign.close_at && isInPast(campaign.close_at)) {
          toast.error('This campaign has ended.')
          return next({ name: 'home' })
        }

        let listId: string = campaign.list_id

        if (!isEmpty(queryParams._list)) {
          listId = queryParams._list as string
        } else if (!isEmpty(to.params.listId)) {
          listId = to.params.listId as string
        }

        to.meta = {
          ...to.meta,
          nav: {
            header: {
              displayLinks: !isPartnershipCampaign(campaign),
              logoImage: isPartnershipCampaign(campaign)
                ? campaign.logo_image
                : undefined,
            },
          },
          props: {
            campaign,
            listId,
          },
        }

        next()
      } catch (error) {
        console.error('Campaign guard failed:', error)
        next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/membership-levels-20',
    name: 'membership-levels-20',
    component: () => import('../views/Discounts/Memberships20View.vue'),
    meta: {
      title: 'Membership Levels 20% Off',
    },
  },
  {
    path: '/product-vip-50',
    name: 'product-vip-50',
    component: () => import('../views/Discounts/MembershipVip50View.vue'),
    meta: {
      title: '50% Off Vip Membership',
    },
  },
  {
    path: '/product-premium-50',
    name: 'product-premium-50',
    component: () => import('../views/Discounts/MembershipVip50View.vue'),
    meta: {
      title: '50% Off Premium Membership',
    },
  },
  {
    path: '/memberships',
    name: 'memberships',
    component: () =>
      import('../views/Memberships/MembershipsLayoutProvider.vue'),
    meta: {
      title: 'Memberships',
      nav: {
        sidebar: {
          display: true,
          text: 'Memberships',
          icon: Icons.Star,
          category: 'default',
          order: 1,
        },
      },
    },
  },
  {
    path: '/secure-checkout',
    name: 'checkout',
    component: () => import('../views/Checkout/CheckoutView.vue'),
    meta: {
      title: 'Checkout',
    },
    props: (
      route: RouteRecordProps<{ plan: Plan; useSolidgateModal: boolean }>
    ) => {
      const props = route.meta.props
      return {
        plan: props.plan,
        useSolidgateModal: props.useSolidgateModal,
      }
    },
    async beforeEnter(
      to: RouteLocation,
      _from: RouteLocation,
      next: NavigationGuardNext
    ) {
      try {
        const plansStore = usePlansStore()
        const userStore = useUserStore()

        let plan: Nullable<Plan> = plansStore.getPlanByTier('standard')

        if (!isEmpty(to.query.plan)) {
          plan = plansStore.getPlan(
            to.query.plan as string,
            Number(to.query.affiliate as string)
          )
        }

        if (!plan) {
          console.error('no plan found')
          return next({ name: 'not-found' })
        }

        // Check if user should use Solidgate based on feature flag and user platform
        const shouldUseSolidgate = userStore.shouldUseSolidgate

        to.meta.props = {
          plan,
          useSolidgateModal: shouldUseSolidgate,
        }

        next()
      } catch (e) {
        console.error(e)
        // redirect to NotFoundView
        return next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/checkout',
    name: 'product-checkout',
    component: () => import('../features/checkout/CheckoutEntry.vue'),
    meta: {
      title: (to: RouteLocation): string =>
        to.query.product === 'deposit' ? 'Deposit' : 'Checkout',
      nav: {
        header: { backButton: true },
      },
    },
    beforeEnter(
      to: RouteLocation,
      _from: RouteLocation,
      next: NavigationGuardNext
    ) {
      // Per-product feature-flag gate. Deposits also require the deposit section
      // and a logged-in customer; plan resolution lives in the subscription host.
      if (to.query.product === 'deposit') {
        if (
          !isDepositPageEnabled() ||
          !SettingsService.isSectionEnabled('deposit')
        ) {
          return next({ name: 'not-found' })
        }
        if (!useUserStore().isLoggedIn) {
          return next({ name: 'home' })
        }
        return next()
      }

      if (!isCheckoutPageEnabled()) {
        return next({ name: 'not-found' })
      }
      next()
    },
    props: (route: RouteLocation) => ({
      product:
        typeof route.query.product === 'string' ? route.query.product : null,
      planCode: typeof route.query.plan === 'string' ? route.query.plan : null,
      affiliate: Array.isArray(route.query.affiliate)
        ? route.query.affiliate[0]
        : route.query.affiliate,
      drawCode: typeof route.query.draw === 'string' ? route.query.draw : null,
    }),
  },
  {
    path: '/payment-recovery',
    name: 'payment-recovery',
    component: () => import('../views/PaymentRecoveryView.vue'),
    meta: {
      title: 'Payment Recovery',
    },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/PasswordResetView.vue'),
    meta: {
      title: 'Password Reset',
    },
  },
  {
    path: '/referral-box',
    name: 'referral-box',
    component: () => import('../views/ReferralBoxView.vue'),
    meta: {
      title: 'Referral Box',
      nav: {
        header: { display: false },
        footer: { display: false },
      },
    },
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import('../views/WelcomeOfferView.vue'),
    meta: {
      title: 'Welcome',
      nav: {
        header: { display: false },
        footer: { display: false },
      },
    },
  },
  {
    path: '/request-password-reset',
    name: 'request-password-reset',
    component: () => import('../views/PasswordResetRequestView.vue'),
    meta: {
      title: 'Password Reset',
    },
  },
  {
    path: '/request-password-reset',
    name: 'request-password-reset-immediate',
    component: () => import('../views/PasswordResetRequestImmediateView.vue'),
    meta: {
      title: 'Password Reset',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: {
      title: 'Login',
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      title: 'Logout',
    },
    beforeEnter: async () => {
      const alertStore = useAlertStore()

      try {
        const userStore = useUserStore()
        const router = useRouter()
        const success = await userStore.logOut(false)

        if (success) {
          await router.push({ name: 'home' })
        } else {
          alertStore.addErrorAlert()
        }
      } catch (e: any) {
        alertStore.addErrorAlert({ message: e.message })
      }
    },
  },
  ...instantWinRoutes,
  ...dailySpinsRoutes,
  ...termsAndConditionsRoutes,
  {
    path: '/impersonate',
    name: 'impersonate',
    beforeEnter: async (to: RouteLocation) => {
      const router = useRouter()
      const alertStore = useAlertStore()
      const userStore = useUserStore()
      const cookies = useCookies(['authToken'], {
        autoUpdateDependencies: true,
      })

      const ticket = Array.isArray(to.query.ticket)
        ? to.query.ticket[0]
        : to.query.ticket

      if (typeof ticket !== 'string' || ticket.length === 0) {
        alertStore.addErrorAlert({ message: 'Invalid impersonation ticket' })

        await router.push({ name: 'home' })
        return
      }

      const { data, error } = await fetcher.post<{ auth_token: string }>(
        toPortalUrl('/impersonation/exchange'),
        {
          ticket,
        }
      )

      if (error.value || !data.value?.auth_token) {
        alertStore.addErrorAlert({
          message: 'Unable to start impersonation session',
        })

        await router.push({ name: 'home' })
        return
      }

      cookies.set('authToken', data.value.auth_token, { path: '/' })

      try {
        await userStore.fetchDetails(true)
      } catch {
        cookies.remove('authToken', { path: '/' })
        userStore.$reset()

        alertStore.addErrorAlert({
          message: 'Unable to load impersonated account details',
        })

        await router.push({ name: 'home' })
        return
      }

      await router.push({ name: 'members-zone' })
    },
  },
  {
    path: '/r/:referralValue',
    name: 'customer-referral-redirect',
    beforeEnter: referralRedirectGuard('referralValue'),
  },
  {
    path: '/share/:referralValue',
    name: 'lead-referral-redirect',
    beforeEnter: referralRedirectGuard('referralValue'),
  },
  {
    path: '/rewards/:slug',
    name: 'reward-claim',
    component: () => import('../views/RewardsClaimView.vue'),
  },
  {
    // https://router.vuejs.org/guide/essentials/dynamic-matching#Catch-all-404-Not-found-Route
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    // // preserve current path and remove the first char to avoid the target URL starting with `//`
    // params: { pathMatch: route.path.substring(1).split('/') },
    // // preserve existing query and hash if any
    // query: route.query,
    // hash: route.hash,
  },
] as Readonly<RouteRecordRaw[]>

export default routes.filter(
  (route) => !SettingsService.disabledRoutes.includes(route.name as string)
)
